/* src/components/pages/Extraerdjcli.jsx - Modificado el 26-01-2026 */
/* ------ scareping Async para declaraciones juardas clientes ----- */

import React, { useState, useEffect, useCallback } from "react";

import { Box, Button, Stack, Typography, TextField } from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";

import { RiFileSearchLine } from "react-icons/ri";

import api from "../../services/api.js";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";

import { CustomToolbar } from "../../components/datagrid/CustomToolbar";

import { normalizeRut } from "../utils/rutUtils";

/* ===================================================== */
/* Extraerdjcli */
/* ===================================================== */

const Extraerdjcli = () => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  const rut = user?.usuario || "";

  /* Bitácora */
  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Server pagination */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* ===================================================== */
  /* Fetch Bitácora */
  /* ===================================================== */

  const fetchBitacora = useCallback(async () => {
    if (!rut) return;

    setLoading(true);

    try {
      const RutCli = rut
        .replace(/\./g, "")
        .replace("-", "")
        .padStart(10, "0")
        .toUpperCase();

      const { data } = await api.post("/dj/getbitacora", {
        RutCli,
        RutEnr: "",
        Tipousr: 1,
        page,
        pageSize,
      });

      if (data?.success) {
        setBitacora(data.rows || []);
        setRowCount(data.total || 0);
      } else {
        setBitacora([]);
        setRowCount(0);
      }
    } catch {
      showFeedback({
        message: "No fue posible obtener la bitácora DJ",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [rut, page, pageSize, showFeedback]);

  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);

  /* ===================================================== */
  /* Solicitar Extractor Async */
  /* ===================================================== */

  const solicitarDJ = async () => {
    try {
      const RutCli = normalizeRut(user.usuario);

      showFeedback({
        message: "🚀 Iniciando extractor DJ...",
        severity: "info",
      });

      /* Start Job */
      const { data } = await api.post("/extraerdj/start", {
        RutCli,
        RutEnr: "",
        Tipousr: 1, /* Cliente */
        page: 0,
        pageSize: 20,
      });

      if (!data?.success) {
        showFeedback({
          message: data?.message || "No se pudo iniciar DJ",
          severity: "error",
        });
        return;
      }

      const jobId = data.jobId;


      
      /* Polling status */
      const interval = setInterval(async () => {
        const res = await api.get(`/extraerdj/status/${jobId}`);

        if (res.data.status === "completed") {
          clearInterval(interval);

          showFeedback({
            message: "✅ Extractor DJ completado",
            severity: "success",
          });

          fetchBitacora();
        }

        if (res.data.status === "failed") {
          clearInterval(interval);

          showFeedback({
            message: "❌ Error en extractor DJ",
            severity: "error",
          });

          fetchBitacora();
        }
      }, 2000);

    } catch (err) {
      showFeedback({
        message: "Error inesperado iniciando DJ",
        severity: "error",
      });
    }
  };

  /* ===================================================== */
  /* Columns */
  /* ===================================================== */

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fecha",
      headerName: "Fecha Actualización",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "—";
        return new Date(params.value).toLocaleString("es-CL");
      },
    },
    {
      field: "tipo",
      headerName: "Tipo",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "rutcli",
      headerName: "Rut Cliente",
      width: 130,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "rutenr",
      headerName: "Rut Enrolado",
      width: 130,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "detalle",
      headerName: "Detalle",
      flex: 1,
      minWidth: 280,
      headerAlign: "center",
    },
    {
      field: "nombre",
      headerName: "Razón Social",
      flex: 1,
      minWidth: 280,
      align: "left",
      headerAlign: "center",
    },
  ];

  /* ===================================================== */
  /* Render */
  /* ===================================================== */

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <TextField
          label="Rut"
          value={rut}
          size="small"
          disabled
          sx={{
            minWidth: 150,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },
          }}
        />

        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          sx={{
            minWidth: 260,
            flexGrow: 1,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },
          }}
        />

        <Button
          variant="contained"
          startIcon={<RiFileSearchLine size={18} />}
          onClick={solicitarDJ}
          sx={{
            height: 40,
            backgroundColor: (theme) => theme.custom.ui.primaryAction,
            "&:hover": {
              backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
            },
          }}
        >
          Solicitar
        </Button>
      </Stack>

      {/* Bitácora */}
      <Box sx={{ height: "calc(100vh - 210px)" }}>
        <DataGrid
          rows={bitacora}
          columns={columns}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page);
            setPageSize(pageSize);
          }}
          slots={{ toolbar: CustomToolbar }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString("es-CL")} ejecuciones registradas
      </Typography>
    </Box>
  );
};

export default Extraerdjcli;
